import React, { useState, useRef } from 'react';
import '../styles/home.css';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Spinner from '@cloudscape-design/components/spinner';
import { addUsage } from './add-usage.js';
import { PDFDocument } from 'pdf-lib';
import constants from '../constants.js';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import { isPdfFile } from './file-validation';
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// US Letter at 72dpi — used for blank pages so they sit naturally among real pages.
const BLANK_W = 612;
const BLANK_H = 792;

const PdfPageEditor = ({ isNew }) => {
  const [visible, setVisible] = useState(false);
  const [file, setFile] = useState(null);
  // pages: array of { key, kind: 'pdf'|'blank', srcIndex?, thumbnail, width, height }
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitValue, setSubmitValue] = useState('Save PDF');
  const [notify, setNotify] = useState('');
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const fileInputRef = useRef(null);
  const pdfBytesRef = useRef(null);
  const keyCounter = useRef(0);

  const nextKey = () => {
    keyCounter.current += 1;
    return `pg-${keyCounter.current}`;
  };

  const toggleModal = () => {
    setVisible(!visible);
    resetState();
  };

  const resetState = () => {
    setFile(null);
    setPages([]);
    setLoading(false);
    setSubmitValue('Save PDF');
    setNotify('');
    setError(null);
    setSelected(null);
    pdfBytesRef.current = null;
    keyCounter.current = 0;
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;
    if (!isPdfFile(selectedFile)) {
      setNotify(constants.onlyPdfAllowed);
      setError('error');
      return;
    }

    setFile(selectedFile);
    setNotify('');
    setError(null);
    setLoading(true);
    setPages([]);
    setSelected(null);

    try {
      const arrayBuffer = await selectedFile.arrayBuffer();
      // Keep a private copy of the original bytes for the final rebuild.
      pdfBytesRef.current = arrayBuffer.slice(0);

      const pdfJsDoc = await pdfjsLib.getDocument({ data: arrayBuffer, password: '' }).promise;
      const pageCount = pdfJsDoc.numPages;
      const rendered = [];
      for (let i = 0; i < pageCount; i++) {
        const pdfJsPage = await pdfJsDoc.getPage(i + 1);
        const viewport = pdfJsPage.getViewport({ scale: 0.5 });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        context.fillStyle = 'white';
        context.fillRect(0, 0, canvas.width, canvas.height);
        await pdfJsPage.render({ canvasContext: context, viewport }).promise;
        rendered.push({
          key: nextKey(),
          kind: 'pdf',
          srcIndex: i,
          thumbnail: canvas.toDataURL('image/png'),
          width: viewport.width,
          height: viewport.height,
        });
        canvas.width = 0;
        canvas.height = 0;
      }
      setPages(rendered);
    } catch (err) {
      console.error('Failed to load PDF:', err);
      setNotify(constants.encryptedFile);
      setError('error');
    } finally {
      setLoading(false);
    }
  };

  const insertBlankAt = (index) => {
    const blank = {
      key: nextKey(),
      kind: 'blank',
      thumbnail: null,
      width: BLANK_W / 2,
      height: BLANK_H / 2,
    };
    const next = [...pages];
    next.splice(index, 0, blank);
    setPages(next);
  };

  const deletePage = (index) => {
    const next = pages.filter((_, i) => i !== index);
    setPages(next);
    setSelected(null);
  };

  const savePdf = async () => {
    if (!pdfBytesRef.current || pages.length === 0) return;
    setSubmitValue(<Spinner />);
    try {
      const srcDoc = await PDFDocument.load(pdfBytesRef.current);
      const outDoc = await PDFDocument.create();

      // Copy every source page up front so we can place them in any order.
      const srcIndices = srcDoc.getPageIndices();
      const copiedSrcPages = await outDoc.copyPages(srcDoc, srcIndices);

      for (const page of pages) {
        if (page.kind === 'pdf') {
          outDoc.addPage(copiedSrcPages[page.srcIndex]);
        } else {
          outDoc.addPage([BLANK_W, BLANK_H]);
        }
      }

      const pdfBytes = await outDoc.save();
      const blob = new Blob([pdfBytes], { type: constants.pdfFileType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `edited_${file?.name || 'document.pdf'}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setSubmitValue('Saved');
      addUsage('AWESOME PDF TOOLS PageEditor', '1.0', 1, window.location.href);
      setTimeout(() => {
        setVisible(false);
        resetState();
      }, 1200);
    } catch (err) {
      console.error('Save failed:', err);
      setNotify('Could not save the PDF. The file may be password protected.');
      setError('error');
      setSubmitValue('Save PDF');
    }
  };

  const pdfPageCount = pages.filter((p) => p.kind === 'pdf').length;
  const blankPageCount = pages.filter((p) => p.kind === 'blank').length;

  return (
    <div>
      <div className="service-card" onClick={toggleModal}>
        {isNew && <span className="badge badge-new">NEW</span>}
        <div className="service-card-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="#FF9900">
            <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11zm-5-3v-2h-2v2H9v2h2v2h2v-2h2v-2h-2z" />
          </svg>
        </div>
        <h3 className="service-card-title">PDF Page Editor</h3>
        <p className="service-card-desc">Add blank pages or delete pages while viewing your PDF</p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
          </svg>
        </div>
      </div>

      <Modal
        onDismiss={toggleModal}
        visible={visible}
        size="large"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={toggleModal}>
                Cancel
              </Button>
              <Button variant="primary" disabled={pages.length === 0 || error} onClick={savePdf}>
                {submitValue}
              </Button>
            </SpaceBetween>
          </Box>
        }
        header="PDF Page Editor"
      >
        <div>
          <div className="bordered-box">
            <h6 className="container-header">📄 Choose PDF</h6>
            <FormField label="Upload a PDF to view, add, and delete pages" errorText={notify}>
              <input ref={fileInputRef} type="file" accept=".pdf,application/pdf" onChange={handleFileChange} />
            </FormField>
            {file && pages.length > 0 && (
              <div style={{ marginTop: '10px', fontSize: '13px', color: '#565959' }}>
                {pdfPageCount} original page{pdfPageCount === 1 ? '' : 's'}
                {blankPageCount > 0 && ` + ${blankPageCount} blank`} = {pages.length} total
              </div>
            )}
          </div>

          {loading && (
            <SpaceBetween size="m" direction="vertical" alignItems="center">
              <Spinner size="large" />
              <Box variant="p" color="text-body-secondary">
                Rendering pages...
              </Box>
            </SpaceBetween>
          )}

          {pages.length > 0 && !loading && (
            <div className="bordered-box">
              <h6 className="container-header">🧩 Pages — click a page to select, use the buttons to add or delete</h6>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '14px',
                  padding: '16px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '12px',
                  minHeight: '160px',
                  background: 'linear-gradient(to bottom, #fafafa, #f5f5f5)',
                }}
              >
                {pages.map((page, index) => (
                  <div key={page.key} style={{ display: 'flex', alignItems: 'center' }}>
                    {/* Insert-before slot */}
                    <button
                      onClick={() => insertBlankAt(index)}
                      title="Insert blank page here"
                      style={{
                        width: '22px',
                        height: '150px',
                        marginRight: '4px',
                        border: '1px dashed #c7c7c7',
                        borderRadius: '6px',
                        background: 'transparent',
                        color: '#FF9900',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        lineHeight: 1,
                      }}
                    >
                      +
                    </button>

                    {/* Page card */}
                    <div
                      onClick={() => setSelected(selected === page.key ? null : page.key)}
                      style={{
                        width: '120px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        backgroundColor: '#fff',
                        border: `2px solid ${selected === page.key ? '#FF9900' : '#e0e0e0'}`,
                        boxShadow:
                          selected === page.key
                            ? '0 4px 12px rgba(255, 153, 0, 0.25)'
                            : '0 2px 5px rgba(15, 17, 17, 0.1)',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <div
                        style={{
                          width: '100%',
                          height: '150px',
                          overflow: 'hidden',
                          backgroundColor: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {page.kind === 'pdf' ? (
                          <img
                            src={page.thumbnail}
                            alt={`Page ${index + 1}`}
                            style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
                          />
                        ) : (
                          <div
                            style={{
                              width: '88%',
                              height: '92%',
                              border: '1px dashed #c7c7c7',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#9aa0a6',
                              fontSize: '12px',
                              textAlign: 'center',
                              background:
                                'repeating-linear-gradient(45deg, #fcfcfc, #fcfcfc 8px, #f4f4f4 8px, #f4f4f4 16px)',
                            }}
                          >
                            Blank
                            <br />
                            Page
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          background:
                            page.kind === 'blank'
                              ? 'linear-gradient(to right, #8a6d3b, #b08d57)'
                              : 'linear-gradient(to right, #232F3E, #37475A)',
                          color: '#fff',
                          fontSize: '11px',
                          fontWeight: '600',
                          textAlign: 'center',
                          padding: '4px 0',
                        }}
                      >
                        {page.kind === 'blank' ? 'Blank' : 'Page'} {index + 1}
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: '4px',
                          padding: '5px 4px',
                          backgroundColor: '#fafafa',
                          borderTop: '1px solid #eee',
                        }}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deletePage(index);
                          }}
                          title="Delete page"
                          style={{
                            width: '28px',
                            height: '28px',
                            border: '1px solid #CC0C39',
                            borderRadius: '6px',
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0,
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="#CC0C39">
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Insert-after slot (only on the last page) */}
                    {index === pages.length - 1 && (
                      <button
                        onClick={() => insertBlankAt(index + 1)}
                        title="Add blank page at end"
                        style={{
                          width: '22px',
                          height: '150px',
                          marginLeft: '4px',
                          border: '1px dashed #c7c7c7',
                          borderRadius: '6px',
                          background: 'transparent',
                          color: '#FF9900',
                          fontSize: '18px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          lineHeight: 1,
                        }}
                      >
                        +
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ marginTop: '12px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <Button onClick={() => insertBlankAt(pages.length)} iconName="add-plus">
                  Add blank page at end
                </Button>
                {selected && (
                  <Button
                    onClick={() => {
                      const idx = pages.findIndex((p) => p.key === selected);
                      if (idx !== -1) deletePage(idx);
                    }}
                    variant="normal"
                  >
                    Delete selected page
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default PdfPageEditor;
